import React, { useState, useEffect, useMemo } from 'react';
import { CalendarDays, Landmark, FileText } from 'lucide-react';

// Updated ALL_MILESTONES with Haitian History
const ALL_MILESTONES = [
  {
    id: 1,
    date: "1791-08-21",
    title: "Beginning of the Haitian Revolution",
    description: "A massive slave revolt begins in the northern plains of Saint-Domingue, marking the start of the Haitian Revolution, the only successful slave uprising leading to the founding of a state.",
    imageUrl: "https://placehold.co/150x100/8B4513/FFFFFF?text=Revolution+Begins",
    category: "Revolution",
  },
  {
    id: 2,
    date: "1793-08-29",
    title: "Sonthonax Abolishes Slavery",
    description: "French Commissioner Léger-Félicité Sonthonax proclaims the abolition of slavery in the northern province of Saint-Domingue, a radical move to gain support of the enslaved population.",
    imageUrl: "https://placehold.co/150x100/A0522D/FFFFFF?text=Slavery+Abolished",
    category: "Revolution",
  },
  {
    id: 3,
    date: "1801-07-07",
    title: "Toussaint Louverture's Constitution",
    description: "Toussaint Louverture promulgates a constitution for Saint-Domingue, declaring himself governor-for-life and laying the groundwork for an autonomous state, while still nominally under French rule.",
    imageUrl: "https://placehold.co/150x100/CD853F/FFFFFF?text=Louverture+Constitution",
    category: "Governance",
  },
  {
    id: 4,
    date: "1804-01-01",
    title: "Haitian Declaration of Independence",
    description: "Jean-Jacques Dessalines proclaims the independence of Haiti, making it the first free black republic in the world and the second independent nation in the Americas.",
    imageUrl: "https://placehold.co/150x100/D2691E/FFFFFF?text=Independence",
    category: "Nationhood",
  },
  {
    id: 5,
    date: "1806-10-17",
    title: "Assassination of Dessalines",
    description: "Emperor Jean-Jacques Dessalines is assassinated, leading to a period of division where Haiti is split into a northern kingdom and a southern republic.",
    imageUrl: "https://placehold.co/150x100/800000/FFFFFF?text=Dessalines+Death",
    category: "Conflict",
  },
  {
    id: 6,
    date: "1820-10-08",
    title: "Unification under Jean-Pierre Boyer",
    description: "Following the death of Henri Christophe, Jean-Pierre Boyer unifies the northern and southern parts of Haiti, ruling over the entire island of Hispaniola after 1822.",
    imageUrl: "https://placehold.co/150x100/4682B4/FFFFFF?text=Haiti+Unified",
    category: "Unification",
  },
  {
    id: 7,
    date: "1825-07-11",
    title: "French Recognition and Indemnity",
    description: "France, under King Charles X, officially recognizes Haitian independence but demands an indemnity of 150 million francs in exchange, a debt that would burden Haiti for decades.",
    imageUrl: "https://placehold.co/150x100/B0C4DE/FFFFFF?text=French+Indemnity",
    category: "Politics",
  },
  {
    id: 8,
    date: "1915-07-28",
    title: "U.S. Occupation of Haiti Begins",
    description: "Citing political instability, the United States invades and occupies Haiti. The occupation lasts for 19 years, until 1934.",
    imageUrl: "https://placehold.co/150x100/708090/FFFFFF?text=US+Occupation",
    category: "Occupation",
  },
  {
    id: 9,
    date: "1934-08-01",
    title: "End of U.S. Occupation",
    description: "The United States officially ends its military occupation of Haiti, though American fiscal control persists for several more years.",
    imageUrl: "https://placehold.co/150x100/778899/FFFFFF?text=Occupation+Ends",
    category: "Politics",
  },
  {
    id: 10,
    date: "1957-09-22",
    title: "François 'Papa Doc' Duvalier Elected",
    description: "François Duvalier, known as 'Papa Doc,' is elected president, beginning a nearly three-decade-long dictatorial rule by the Duvalier family.",
    imageUrl: "https://placehold.co/150x100/2F4F4F/FFFFFF?text=Papa+Doc",
    category: "Dictatorship",
  },
  {
    id: 11,
    date: "1971-04-21",
    title: "Jean-Claude 'Baby Doc' Duvalier Assumes Presidency",
    description: "Following his father's death, Jean-Claude Duvalier ('Baby Doc') becomes president-for-life at the age of 19, continuing the family's authoritarian regime.",
    imageUrl: "https://placehold.co/150x100/556B2F/FFFFFF?text=Baby+Doc",
    category: "Dictatorship",
  },
  {
    id: 12,
    date: "1986-02-07",
    title: "Ouster of Jean-Claude Duvalier",
    description: "A popular uprising forces Jean-Claude Duvalier to flee Haiti, ending 29 years of Duvalier family rule and ushering in a period of political transition and instability.",
    imageUrl: "https://placehold.co/150x100/8FBC8F/FFFFFF?text=Duvalier+Ousted",
    category: "Transition",
  },
  {
    id: 13,
    date: "1990-12-16",
    title: "Jean-Bertrand Aristide Elected President",
    description: "Jean-Bertrand Aristide, a former priest, wins Haiti's first free and fair democratic elections, bringing hope for change and reform.",
    imageUrl: "https://placehold.co/150x100/3CB371/FFFFFF?text=Aristide+Elected",
    category: "Democracy",
  },
  {
    id: 14,
    date: "1991-09-29",
    title: "First Aristide Coup d'état",
    description: "President Jean-Bertrand Aristide is overthrown in a military coup led by Raoul Cédras, just months after taking office.",
    imageUrl: "https://placehold.co/150x100/98FB98/FFFFFF?text=Aristide+Coup",
    category: "Conflict",
  },
  {
    id: 15,
    date: "1994-10-15",
    title: "Aristide Restored to Power",
    description: "Following a U.S.-led military intervention (Operation Uphold Democracy), Jean-Bertrand Aristide is restored to the presidency.",
    imageUrl: "https://placehold.co/150x100/00FA9A/FFFFFF?text=Aristide+Restored",
    category: "Democracy",
  },
  {
    id: 16,
    date: "2004-02-29",
    title: "Aristide Ousted Again",
    description: "Amidst a rebellion and political turmoil, Jean-Bertrand Aristide is ousted from power for a second time and leaves the country.",
    imageUrl: "https://placehold.co/150x100/66CDAA/FFFFFF?text=Aristide+Ousted+2",
    category: "Conflict",
  },
  {
    id: 17,
    date: "2010-01-12",
    title: "Devastating Earthquake",
    description: "A catastrophic magnitude 7.0 earthquake strikes Haiti, primarily affecting Port-au-Prince and surrounding areas, causing widespread death and destruction.",
    imageUrl: "https://placehold.co/150x100/FF6347/FFFFFF?text=Earthquake",
    category: "Disaster",
  },
  {
    id: 18,
    date: "2017-02-07",
    title: "Jovenel Moïse Becomes President",
    description: "Jovenel Moïse is sworn in as President of Haiti after a prolonged electoral process, facing significant political and economic challenges.",
    imageUrl: "https://placehold.co/150x100/4169E1/FFFFFF?text=Moise+Presidency",
    category: "Politics",
  },
  {
    id: 19,
    date: "2021-07-07",
    title: "Assassination of President Jovenel Moïse",
    description: "President Jovenel Moïse is assassinated at his private residence, plunging the country into a deeper political crisis.",
    imageUrl: "https://placehold.co/150x100/B22222/FFFFFF?text=Moise+Assassinated",
    category: "Crisis",
  },
  {
    id: 20,
    date: "2024-03-11",
    title: "Prime Minister Ariel Henry Announces Resignation Amidst Gang Violence",
    description: "Prime Minister Ariel Henry announces he will resign once a transitional presidential council is created, following weeks of escalating gang violence and a breakdown of order.",
    imageUrl: "https://placehold.co/150x100/A9A9A9/FFFFFF?text=Henry+Resigns",
    category: "Crisis",
  }
];


// Thumbnail Component
const Thumbnail = ({ src, alt }) => (
  <img 
    src={src} 
    alt={alt} 
    className="w-full h-32 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300" 
    onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x100/CCCCCC/FFFFFF?text=Image+Error"; }}
  />
);

// Milestone Component
const Milestone = ({ milestone, index }) => {
  const isEven = index % 2 === 0;
  return (
    // Each milestone container, handling alternating layout for medium screens and up
    <div className={`flex items-start my-8 md:my-12 relative ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
      {/* Decorative element for the timeline connector (line part), hidden on small screens */}
      <div className={`hidden md:flex flex-col items-center w-16`}>
        <div className="w-1 h-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      
      {/* Dot on the timeline, positioned absolutely on small screens, relatively on medium+ */}
      <div className={`absolute md:relative left-0 md:left-auto ${isEven ? 'md:ml-[-33px]' : 'md:mr-[-33px]'} top-0 md:top-1/2 md:-translate-y-1/2 z-10`}>
        <div className="w-8 h-8 bg-sky-500 dark:bg-sky-400 rounded-full border-4 border-white dark:border-gray-800 flex items-center justify-center shadow-lg">
          <Landmark size={16} className="text-white" /> {/* Icon representing a milestone */}
        </div>
      </div>

      {/* Milestone Content Box */}
      <div className={`bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl w-full md:w-[calc(50%-3rem)] ${isEven ? 'ml-12 md:ml-0' : 'mr-12 md:mr-0'}`}>
        <Thumbnail src={milestone.imageUrl} alt={milestone.title} />
        <h3 className="text-2xl font-semibold text-sky-600 dark:text-sky-400 mt-4 mb-2">{milestone.title}</h3>
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3">
          <CalendarDays size={16} className="mr-2 text-sky-500 dark:text-sky-400" />
          {/* Formatting the date for display */}
          <span>{new Date(milestone.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{milestone.description}</p>
        <div className="mt-3 text-xs text-sky-500 dark:text-sky-300 uppercase font-medium">
          Category: {milestone.category}
        </div>
      </div>
    </div>
  );
};


// Timeline Component
const Timeline = ({ milestones }) => {
  if (!milestones || milestones.length === 0) {
    return <p className="text-center text-gray-500 dark:text-gray-400 py-8">No milestones to display.</p>;
  }

  let lastRenderedYear = null; // Keep track of the last year for placing year anchors

  return (
    <div className="relative px-4 py-8">
      {/* Central vertical line for medium and larger screens (decorative) */}
      <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-1 bg-gray-300 dark:bg-gray-600 transform -translate-x-1/2"></div>
      
      {/* Vertical line for small screens (decorative) */}
      <div className="md:hidden absolute top-0 bottom-0 left-4 w-1 bg-gray-300 dark:bg-gray-600"></div>
      
      {milestones.map((milestone, index) => {
        const currentYear = new Date(milestone.date).getFullYear();
        let yearAnchor = null;
        // If this milestone is the first of a new year, create an anchor div for scrolling
        if (currentYear !== lastRenderedYear) {
          yearAnchor = <div id={`year-${currentYear}`} className="scroll-mt-[100px] -mt-[100px] h-0"></div>; // Anchor for scrolling, with top margin. Negative top margin to compensate for its own height if any.
          lastRenderedYear = currentYear;
        }
        return (
          // Using React.Fragment to group the optional anchor and the milestone
          <React.Fragment key={milestone.id}>
            {yearAnchor}
            <Milestone milestone={milestone} index={index} />
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Main App Component
export default function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Effect to toggle 'dark' class on HTML element for TailwindCSS dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Add html { scroll-behavior: smooth; } for smooth scrolling via CSS
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto'; // Cleanup on unmount
    };
  }, [isDarkMode]);

  // Extract unique years from milestones for shortcut buttons
  const uniqueYears = useMemo(() => {
    const years = new Set(ALL_MILESTONES.map(m => new Date(m.date).getFullYear()));
    return Array.from(years).sort((a, b) => a - b); // Sort years chronologically
  }, []); // Depends only on ALL_MILESTONES

  // Function to scroll to a specific year in the timeline
  const scrollToYear = (year) => {
    const element = document.getElementById(`year-${year}`);
    if (element) {
      // Smooth scroll to the year anchor. The offset is handled by scroll-mt on the anchor.
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  const toggleDarkMode = () => setIsDarkMode(!isDarkMode);

  return (
    <div className={`min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-300 font-inter`}>
      <header className="bg-sky-600 dark:bg-sky-800 text-white p-6 shadow-md sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-3xl font-bold flex items-center">
            <FileText size={32} className="mr-3" />
            Haitian History Timeline
          </h1>
          <button 
            onClick={toggleDarkMode}
            className="p-2 rounded-md hover:bg-sky-700 dark:hover:bg-sky-700 transition-colors"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? '☀️' : '🌙'} {/* Sun/Moon emoji for dark mode toggle */}
          </button>
        </div>
      </header>

      <main className="container mx-auto py-8 px-4"> {/* Adjusted padding for sticky elements */}
         {/* Year Shortcut Navigation */}
        {uniqueYears.length > 0 && (
          <div className="mb-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl shadow-lg sticky top-[88px] z-40 backdrop-blur-sm bg-opacity-80 dark:bg-opacity-80"> {/* Approx header height (p-6 * 2 for top/bottom + text-3xl line height) */}
            <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-3 text-center">Quick Jump to Year</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {uniqueYears.map(year => (
                <button
                  key={year}
                  onClick={() => scrollToYear(year)}
                  className="px-4 py-2 bg-sky-500 hover:bg-sky-600 dark:bg-sky-600 dark:hover:bg-sky-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm"
                  aria-label={`Scroll to year ${year}`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Main timeline content area. Top padding is adjusted to be below the sticky year shortcuts */}
        <div className="bg-white dark:bg-gray-800 shadow-2xl rounded-lg overflow-hidden mt-8 md:mt-0"> {/* Added margin-top for spacing from sticky nav if it's very tall */}
          <Timeline milestones={ALL_MILESTONES} /> {/* Pass all milestones directly */}
        </div>

      </main>
      
      <footer className="text-center py-6 text-gray-600 dark:text-gray-400 text-sm">
        <p>&copy; {new Date().getFullYear()} Timeline App. All rights reserved.</p>
      </footer>
    </div>
  );
}
